import { createBrowserRouter, type RouteObject } from 'react-router-dom';
import i18n, { t } from 'i18next';
import type { BreadcrumbsRoute } from 'use-react-router-breadcrumbs';

import AppLayout from './layouts/app/AppLayout.tsx';
import { RouterError } from './pages/error/RouterError.tsx';
import { LocationsRoute } from './pages/locations/Locations.route.tsx';
import { customerTabs } from './tabs/customerTabs.ts';
import { dashboardTabs } from './tabs/dashboardTabs.tsx';
import { fleetTabs } from './tabs/fleetTabs.tsx';
import { locationAdministrationTabs } from './tabs/locationAdministrationTabs.ts';
import { orderTabs } from './tabs/orderTabs.ts';
import { rolesAndPermissionsTabs } from './tabs/rolesAndPermissionsTabs.ts';
import { scheduleTabs } from './tabs/scheduleTabs.ts';
import {
  stockItemsTabs,
  stockOverviewTabs,
  stockTakesTabs,
} from './tabs/stockTabs.tsx';
import { surveyBuilderTabs } from './tabs/surveyBuilderTabs.tsx';
import { surveyConfigTabs } from './tabs/surveyConfigTabs.tsx';
import { userTabs } from './tabs/userTabs.ts';
import { clientConfig } from './clientConfig.ts';

const lazyLoad =
  <T extends Record<string, React.ComponentType>>(
    importFn: () => Promise<T>,
    exportName: keyof T & string = 'default' as keyof T & string
  ) =>
  () =>
    importFn().then((m) => ({ Component: m[exportName] }));

const IndexRoute: BreadcrumbsRoute = {
  lazy: lazyLoad(() => import('./pages/IndexPage.tsx')),
  errorElement: <RouterError />,
  index: true,
};

const ScheduleBoardRoute: BreadcrumbsRoute = {
  path: '/scheduling',
  lazy: lazyLoad(() => import('./pages/scheduleBoard/ScheduleBoardPage.tsx')),
  errorElement: <RouterError />,
  children: [
    {
      path: scheduleTabs.scheduleBoard.key,
      lazy: lazyLoad(
        () => import('./components/scheduleBoard/ScheduleBoard.tsx')
      ),
      breadcrumb: scheduleTabs.scheduleBoard.label,
    },
    {
      path: scheduleTabs.workshopView.key,
      lazy: lazyLoad(
        () => import('./components/scheduleBoard/WorkshopView.tsx')
      ),
      breadcrumb: scheduleTabs.workshopView.label,
    },
    {
      path: scheduleTabs.resourceView.key,
      lazy: lazyLoad(
        () =>
          import(
            './components/scheduleResourcesView/ScheduleResourcesView.tsx'
          ),
        'ScheduleResourcesView'
      ),
      breadcrumb: scheduleTabs.resourceView.label,
    },
  ],
};

const DashboardRoute: RouteObject[] = [
  {
    path: '/dashboard',
    lazy: lazyLoad(() => import('./pages/dashboard/DashboardPage.tsx')),
    errorElement: <RouterError />,
    children: [
      {
        path: dashboardTabs.orders.key,
        lazy: lazyLoad(
          () => import('./pages/dashboard/DashboardOrdersTab.tsx'),
          'DashboardOrdersTab'
        ),
        breadcrumb: i18n.t('orders.orders'),
      },
      {
        path: dashboardTabs.jobInsights.key,
        lazy: lazyLoad(
          () =>
            import(
              './components/dashboard/JobInsights/DashboardJobInsightsTab.tsx'
            ),
          'DashboardJobInsightsTab'
        ),
        breadcrumb: i18n.t('jobs.jobInsights'),
      },
      {
        path: dashboardTabs.inventoryInsights.key,
        lazy: lazyLoad(
          () =>
            import(
              './components/dashboard/InventoryInsights/DashboardInventoryInsightsTab.tsx'
            ),
          'DashboardInventoryInsightsTab'
        ),
        breadcrumb: i18n.t('dashboard.inventoryInsights'),
      },
      {
        path: dashboardTabs.fittingStatus.key,
        lazy: lazyLoad(
          () =>
            import(
              './components/dashboard/FittingStatus/DashboardFittingStatusTab.tsx'
            ),
          'DashboardFittingStatusTab'
        ),
        breadcrumb: i18n.t('dashboard.fittingStatus'),
      },
    ] as BreadcrumbsRoute[],
  },
];

const DiscountsRoute: BreadcrumbsRoute = {
  path: '/discounts',
  lazy: lazyLoad(() => import('./pages/discounts/DiscountsPage.tsx')),
  errorElement: <RouterError />,
};

const stockRoute = clientConfig.locale === 'en-US' ? 'inventory' : 'stock';
const stockTakesRoute =
  stockRoute === 'inventory' ? 'inventory-checks' : 'stock-takes';

const StockRoutes: RouteObject[] = [
  {
    path: `/${stockRoute}`,
    lazy: lazyLoad(() => import('./pages/stock/StockItemsPage.tsx')),
    errorElement: <RouterError />,
    children: [
      {
        path: stockItemsTabs.items.key,
        lazy: lazyLoad(
          () => import('./pages/stock/StockItems/StockItemsTab.tsx')
        ),
        breadcrumb: t('items.items'),
      },
      {
        path: stockItemsTabs.profiles.key,
        lazy: lazyLoad(
          () => import('./pages/stock/StockItems/StockProfilesTab.tsx')
        ),
        breadcrumb: t('profiles.profiles'),
      },
      {
        path: stockItemsTabs.warranties.key,
        lazy: lazyLoad(
          () => import('./pages/stock/StockItems/WarrantiesTab.tsx'),
          'WarrantiesPage'
        ),
        errorElement: <RouterError hideToolbar />,
      },
    ] as BreadcrumbsRoute[],
  },
  {
    path: `/${stockRoute}-overview`,
    lazy: lazyLoad(() => import('./pages/stock/StockOverviewPage.tsx')),
    errorElement: <RouterError />,
    children: [
      {
        path: stockOverviewTabs.stockLevels.key,
        lazy: lazyLoad(
          () => import('./pages/stock/StockOverview/StockLevelsTab.tsx')
        ),
        breadcrumb: t('stock.stockLevels'),
      },
      {
        path: stockOverviewTabs.returns.key,
        lazy: lazyLoad(
          () => import('./pages/stock/StockOverview/StockReturnsTab.tsx')
        ),
        breadcrumb: i18n.t('stock.returns.returns'),
      },
      {
        path: stockOverviewTabs.deliveries.key,
        lazy: lazyLoad(
          () => import('./pages/stock/StockOverview/StockDeliveriesTab.tsx')
        ),
        breadcrumb: i18n.t('stock.deliveries.deliveries'),
      },
      {
        path: stockOverviewTabs.transfers.key,
        lazy: lazyLoad(
          () => import('./pages/stock/StockOverview/StockTransfersTab.tsx')
        ),
        breadcrumb: i18n.t('stock.transfers.transfers'),
      },
      {
        path: stockOverviewTabs.adjustments.key,
        lazy: lazyLoad(
          () => import('./pages/stock/StockOverview/StockAdjustmentsTab.tsx')
        ),
        breadcrumb: i18n.t('stock.adjustments.adjustments'),
      },
      {
        path: stockOverviewTabs.quarantine.key,
        lazy: lazyLoad(
          () => import('./pages/stock/StockOverview/StockQuarantineTab.tsx')
        ),
        breadcrumb: i18n.t('stock.quarantine.quarantine'),
      },
      {
        path: stockOverviewTabs.purchaseOrders.key,
        lazy: lazyLoad(
          () => import('./pages/stock/StockOverview/PurchaseOrderTab.tsx')
        ),
        breadcrumb: i18n.t('purchaseOrders.purchaseOrders'),
      },
    ] as BreadcrumbsRoute[],
  },
  {
    path: `/${stockRoute}/manage-${stockTakesRoute}/:id`,
    lazy: lazyLoad(
      () => import('./pages/stock/StockTakeDetails/StockTakeDetailsPage.tsx'),
      'StockTakeDetailsPage'
    ),
    errorElement: <RouterError />,
  },
  {
    path: `/${stockRoute}/manage-${stockTakesRoute}`,
    lazy: lazyLoad(() => import('./pages/stock/StockTakesPage.tsx')),
    errorElement: <RouterError />,
    children: [
      {
        path: stockTakesTabs.stockTakes.key,
        lazy: lazyLoad(
          () => import('./pages/stock/StockTakes/StockTakesTab.tsx')
        ),
        breadcrumb: t('stock.stockTakes.stockTakes'),
      },
      {
        path: stockTakesTabs.stockTakeProfiles.key,
        lazy: lazyLoad(
          () =>
            import(
              './pages/stock/StockTakes/StockTakeProfiles/StockTakeProfilesTab.tsx'
            )
        ),
        breadcrumb: i18n.t('stockTakeProfiles.profiles'),
      },
    ] as BreadcrumbsRoute[],
  },
  {
    path: `/${stockRoute}/categories`,
    lazy: lazyLoad(
      () => import('./pages/stock/StockCategories/StockCategoriesPage.tsx')
    ),
    errorElement: <RouterError />,
  },
  {
    path: '/suppliers',
    lazy: lazyLoad(() => import('./pages/suppliers/SupplierPage.tsx')),
    errorElement: <RouterError />,
  },
];

const UserRoutes: RouteObject[] = [
  {
    path: '/users',
    lazy: lazyLoad(() => import('./pages/users/UsersPage.tsx'), 'UsersPage'),
    errorElement: <RouterError />,
  },
  {
    path: '/users/:userId',
    lazy: lazyLoad(
      () => import('./pages/users/UserDetailsPage.tsx'),
      'UserDetailsPage'
    ),
    errorElement: <RouterError />,
    children: [
      {
        path: userTabs.user.key,
        lazy: lazyLoad(
          () => import('./components/users/UserDetailsTab.tsx'),
          'UserDetailsTab'
        ),
        breadcrumb: i18n.t('users.userDetails'),
      },
      {
        path: userTabs.organisations.key,
        lazy: lazyLoad(
          () => import('./components/users/UserOrganisationTab.tsx'),
          'UserOrganisationTab'
        ),
        breadcrumb: i18n.t('users.userDetails'),
      },
      {
        path: userTabs.skills.key,
        lazy: lazyLoad(
          () => import('./components/users/UserSkillsTab.tsx'),
          'UserSkillsTab'
        ),
        breadcrumb: i18n.t('users.userDetails'),
      },
      {
        path: userTabs.rolesPermissions.key,
        lazy: lazyLoad(
          () => import('./components/users/UserRolesPermissionsTab.tsx'),
          'UserRolesPermissionsTab'
        ),
        breadcrumb: i18n.t('users.userDetails'),
      },
    ] as BreadcrumbsRoute[],
  },
  {
    path: '/quality-groups',
    lazy: lazyLoad(
      () => import('./pages/users/QualityGroupsPage.tsx'),
      'QualityGroupsPage'
    ),
    errorElement: <RouterError />,
  },
  {
    path: '/quality-ratings',
    lazy: lazyLoad(
      () => import('./pages/users/QualityRatingsPage.tsx'),
      'QualityRatingsPage'
    ),
    errorElement: <RouterError />,
  },
];

const FleetRoute: RouteObject[] = [
  {
    path: '/fleet',
    lazy: lazyLoad(
      () => import('./pages/fleet/ManageFleetPage.tsx'),
      'ManageFleetPage'
    ),
    errorElement: <RouterError />,
  },
  {
    path: '/fleet/:id',
    lazy: lazyLoad(
      () => import('./pages/fleet/FleetDetailsPage.tsx'),
      'FleetDetailsPage'
    ),
    errorElement: <RouterError />,
    children: [
      {
        path: fleetTabs.account.key,
        lazy: lazyLoad(
          () => import('./components/fleet/Account/FleetAccountTab.tsx'),
          'FleetAccountTab'
        ),
        breadcrumb: i18n.t('fleet.accountDetails'),
      },
      {
        path: fleetTabs.orders.key,
        lazy: lazyLoad(
          () => import('./components/fleet/Orders/FleetOrdersTab.tsx'),
          'FleetOrdersTab'
        ),
        breadcrumb: i18n.t('fleet.accountDetails'),
      },
      {
        path: fleetTabs.vehicles.key,
        lazy: lazyLoad(
          () => import('./components/fleet/Vehicles/FleetVehiclesTab.tsx'),
          'FleetVehiclesTab'
        ),
        breadcrumb: i18n.t('common.vehicle.vehicles'),
      },
      {
        path: fleetTabs.contacts.key,
        lazy: lazyLoad(
          () => import('./components/fleet/Contacts/FleetContactsTab.tsx'),
          'FleetContactsTab'
        ),
        breadcrumb: i18n.t('fleet.accountDetails'),
      },
      {
        path: fleetTabs.status.key,
        lazy: lazyLoad(
          () => import('./components/fleet/Status/FleetStatusTab.tsx'),
          'FleetStatusTab'
        ),
        breadcrumb: i18n.t('fleet.accountDetails'),
      },
    ] as BreadcrumbsRoute[],
  },
];

const CustomerRoutes: RouteObject[] = [
  {
    path: '/customers/manage-customers/:customerId',
    lazy: lazyLoad(() => import('./pages/customers/CustomerPage.tsx')),
    errorElement: <RouterError />,
    children: [
      {
        path: customerTabs.customerDetails.key,
        breadcrumb: i18n.t('customers.customerDetails.customerDetails'),
        lazy: lazyLoad(
          () =>
            import(
              './components/customers/CustomerDetails/View/ViewCustomerDetailsTab.tsx'
            )
        ),
      },
      {
        path: customerTabs.vehicleDetails.key,
        breadcrumb: i18n.t('customers.customerDetails.customerDetails'),
        lazy: lazyLoad(
          () =>
            import(
              './components/customers/VehicleDetails/VehicleDetailsTab.tsx'
            )
        ),
      },
      {
        path: customerTabs.orders.key,
        breadcrumb: i18n.t('orders.orders'),
        lazy: lazyLoad(
          () => import('./components/customers/Orders/CustomerOrdersTab.tsx')
        ),
      },
      {
        path: customerTabs.audit.key,
        breadcrumb: i18n.t('common.audit.audit'),
        lazy: lazyLoad(
          () =>
            import('./components/customers/AuditDetails/CustomerAuditTab.tsx')
        ),
      },
    ] as BreadcrumbsRoute[],
  },
  {
    path: '/customers',
    lazy: lazyLoad(
      () => import('./pages/customers/ManageCustomersPage.tsx'),
      'ManageCustomersPage'
    ),
    errorElement: <RouterError />,
  },
];

const TechnicianSurveysRoute: RouteObject[] = [
  {
    path: '/technician-surveys/surveys',
    lazy: lazyLoad(
      () => import('./pages/technician-surveys/TechnicianSurveysPage.tsx')
    ),
    errorElement: <RouterError />,
  },
  {
    path: '/technician-surveys/configuration',
    lazy: lazyLoad(
      () => import('./pages/technicians-surveys-config/ConfigurationPage.tsx')
    ),
    errorElement: <RouterError />,
    children: [
      {
        path: surveyConfigTabs.customerTypes.key,
        lazy: lazyLoad(
          () =>
            import(
              './pages/technicians-surveys-config/tabs/CustomerTypesTab.tsx'
            ),
          'CustomerTypesTab'
        ),
      },
      {
        path: surveyConfigTabs.jobTypes.key,
        lazy: lazyLoad(
          () =>
            import('./pages/technicians-surveys-config/tabs/JobTypesTab.tsx'),
          'JobTypesTab'
        ),
      },
      {
        path: surveyConfigTabs.unplannedServiceTypes.key,
        lazy: lazyLoad(
          () =>
            import(
              './pages/technicians-surveys-config/tabs/UnplannedServiceTypesTab.tsx'
            ),
          'UnplannedServiceTypesTab'
        ),
      },
    ] as BreadcrumbsRoute[],
  },
  {
    path: '/technician-surveys/media-library',
    lazy: lazyLoad(() => import('./pages/mediaLibrary/MediaLibraryPage.tsx')),
    errorElement: <RouterError />,
  },
  {
    path: '/technician-surveys/surveys/:id',
    lazy: lazyLoad(
      () => import('./pages/technician-surveys/SurveyBuilderPage.tsx')
    ),
    errorElement: <RouterError />,
    children: [
      {
        path: surveyBuilderTabs.build.key,
        lazy: lazyLoad(
          () => import('./pages/technician-surveys/tabs/buildSurveyTab.tsx'),
          'BuildSurveyTab'
        ),
      },
      {
        path: surveyBuilderTabs.preview.key,
        lazy: lazyLoad(
          () => import('./pages/technician-surveys/tabs/previewSurveyTab.tsx'),
          'PreviewSurveyTab'
        ),
      },
      {
        path: surveyBuilderTabs.journeyMap.key,
        lazy: lazyLoad(
          () =>
            import('./pages/technician-surveys/tabs/surveyJourneyMapTab.tsx'),
          'SurveyJourneyMapTab'
        ),
        breadcrumb: i18n.t('surveys.journeyMap'),
      },
    ] as BreadcrumbsRoute[],
  },
];

const OrderRoutes: RouteObject[] = [
  {
    path: '/orders/manage-orders',
    lazy: lazyLoad(() => import('./pages/orders/ManageOrdersPage.tsx')),
    errorElement: <RouterError />,
  },
  {
    path: '/orders/create',
    lazy: lazyLoad(() => import('./pages/orders/CreateOrderPage.tsx')),
    errorElement: <RouterError />,
  },
  {
    path: '/orders/manage-orders/:id/reschedule/:bookingReference/:scheduleId',
    lazy: lazyLoad(() => import('./pages/orders/OrderReschedulePage.tsx')),
    errorElement: <RouterError />,
  },
  {
    path: '/orders/manage-orders/:id',
    lazy: lazyLoad(() => import('./pages/orders/OrderDetailsPage.tsx')),
    errorElement: <RouterError />,
    children: [
      {
        path: orderTabs.overview.key,
        lazy: lazyLoad(
          () =>
            import(
              './pages/orders/OrderManagement/Overview/OrderOverviewTab.tsx'
            )
        ),
        breadcrumb: i18n.t('orders.orderOverview'),
      },
      {
        path: orderTabs.jobs.key,
        lazy: lazyLoad(
          () => import('./components/orders/Jobs/OrderJobContainer.tsx'),
          'OrderJobContainer'
        ),
        breadcrumb: i18n.t('jobs.jobBuilder'),
        children: [
          {
            path: '',
            lazy: lazyLoad(
              () =>
                import('./pages/orders/OrderManagement/OrderJobBuilderTab.tsx'),
              'OrderJobBuilderTab'
            ),
          },
          {
            path: ':jobId',
            lazy: lazyLoad(
              () => import('./components/orders/Jobs/ViewJob/ViewJob.tsx'),
              'ViewJob'
            ),
          },
        ],
      },
      {
        path: orderTabs.schedule.key,
        lazy: lazyLoad(
          () => import('./pages/orders/OrderManagement/OrderScheduleTab.tsx')
        ),
        breadcrumb: i18n.t('scheduling.schedule'),
      },
      {
        path: orderTabs.notes.key,
        lazy: lazyLoad(
          () => import('./pages/orders/OrderManagement/OrderNotesPageTab.tsx'),
          'OrderNotesPageTab'
        ),
        breadcrumb: i18n.t('notes.notes'),
      },
      {
        path: orderTabs.payments.key,
        lazy: lazyLoad(
          () => import('./pages/orders/OrderManagement/OrderPaymentsTab.tsx')
        ),
        breadcrumb: i18n.t('payments.payments'),
      },
      {
        path: orderTabs.surveys.key,
        lazy: lazyLoad(
          () => import('./components/orders/Survey/OrderSurveyTab.tsx')
        ),
        breadcrumb: i18n.t('surveys.surveys'),
      },
      {
        path: orderTabs.audit.key,
        lazy: lazyLoad(
          () => import('./pages/orders/OrderManagement/OrderAuditTab.tsx')
        ),
        breadcrumb: i18n.t('common.audit.audit'),
      },
      {
        path: orderTabs.inventory.key,
        lazy: lazyLoad(
          () => import('./pages/orders/OrderManagement/OrderInventoryTab.tsx')
        ),
        breadcrumb: i18n.t('products.addInternalProduct'),
      },
    ] as BreadcrumbsRoute[],
  },
];

const StoreAccountingRoute: RouteObject[] = [
  {
    path: '/store-accounting/end-of-day',
    lazy: lazyLoad(
      () => import('./pages/storeAccounting/EodReportingPage.tsx'),
      'EodReportingPage'
    ),
    errorElement: <RouterError />,
  },
  {
    path: '/store-accounting/cash-and-banking',
    lazy: lazyLoad(
      () => import('./pages/cashBanking/CashBankingPage.tsx'),
      'CashBankingPage'
    ),
    errorElement: <RouterError />,
  },
];

const SettingsRoute: RouteObject[] = [
  {
    path: '/configuration-and-integrations',
    lazy: lazyLoad(
      () => import('./components/settings/IntegrationsTab.tsx'),
      'IntegrationsTab'
    ),
    errorElement: <RouterError />,
  },
  {
    path: '/settings/roles-and-permissions',
    lazy: lazyLoad(
      () => import('./pages/settings/RolesAndPermissionsPage.tsx'),
      'RolesAndPermissionsPage'
    ),
    errorElement: <RouterError />,
    children: [
      {
        path: rolesAndPermissionsTabs.roles.key,
        lazy: lazyLoad(
          () => import('./components/settings/RolesTab.tsx'),
          'RolesTab'
        ),
        breadcrumb: i18n.t('users.userDetails'),
      },
      {
        path: rolesAndPermissionsTabs.permissions.key,
        lazy: lazyLoad(
          () => import('./components/settings/PermissionsTab.tsx'),
          'PermissionsTab'
        ),
        breadcrumb: i18n.t('users.userDetails'),
      },
    ] as BreadcrumbsRoute[],
  },
  {
    path: '/settings/locations-administration',
    lazy: lazyLoad(
      () => import('./pages/settings/LocationAdministrationPage.tsx'),
      'LocationsAdministrationPage'
    ),
    errorElement: <RouterError />,
    children: [
      {
        path: locationAdministrationTabs.openingHoursGroup.key,
        lazy: lazyLoad(
          () => import('./pages/locations/OpeningHours/OpeningHoursPage.tsx'),
          'OpeningHoursPage'
        ),
        breadcrumb: i18n.t('settings.locationAdministration.title'),
      },
      {
        path: locationAdministrationTabs.assetTypes.key,
        lazy: lazyLoad(
          () => import('./pages/locations/AssetTypes/AssetTypesPage.tsx'),
          'AssetTypesPage'
        ),
        breadcrumb: i18n.t('settings.locationAdministration.title'),
      },
      {
        path: locationAdministrationTabs.manageJobTypes.key,
        lazy: lazyLoad(
          () =>
            import('./pages/locations/ManageJobTypes/ManageJobTypesPage.tsx'),
          'ManageJobTypesPage'
        ),
        breadcrumb: i18n.t('settings.locationAdministration.title'),
      },
      {
        path: locationAdministrationTabs.priceBeat.key,
        lazy: lazyLoad(
          () => import('./pages/locations/PriceBeat/PriceBeatPage.tsx'),
          'PriceBeatPage'
        ),
        breadcrumb: i18n.t('settings.locationAdministration.title'),
      },
      {
        path: locationAdministrationTabs.locationCategories.key,
        lazy: lazyLoad(
          () =>
            import(
              './pages/locations/LocationCategories/LocationCategories.tsx'
            ),
          'LocationCategories'
        ),
        breadcrumb: i18n.t('settings.locationAdministration.title'),
      },
    ] as BreadcrumbsRoute[],
  },
];

const WarrantiesRoute: RouteObject[] = [
  {
    path: '/warranties',
    lazy: lazyLoad(
      () => import('./pages/stock/StockItems/WarrantiesTab.tsx'),
      'WarrantiesPage'
    ),
    errorElement: <RouterError />,
  },
];

export const routes = [
  {
    path: '/',
    element: <AppLayout />,
    errorElement: <RouterError />,
    children: [
      IndexRoute,
      ScheduleBoardRoute,
      ...TechnicianSurveysRoute,
      ...DashboardRoute,
      ...CustomerRoutes,
      ...OrderRoutes,
      ...UserRoutes,
      ...LocationsRoute,
      DiscountsRoute,
      ...StockRoutes,
      ...FleetRoute,
      ...StoreAccountingRoute,
      ...SettingsRoute,
      ...WarrantiesRoute,
    ],
  },
];

export const router = createBrowserRouter(routes, {
  basename: '/',
});
